import React from 'react';
import { observer } from 'src/utils/mobx-react';
import {
    ListItem,
    LabelWrapper,
    ColumnWrapper,
    ImageContainer,
    ImagePlaceholderIconWrapper,
    ItemDetails,
    ItemDescription,
    MovableSpan,
    DragAndDropIconWrapper,
    ArrowIconWrapperTemporary,
    ToggleWrapper,
    ActionsWrapper,
} from 'src/domains/layouts/shared/cms/list/List.style';
import { MoreActions } from 'src/domains/layouts/shared/moreActions/MoreActions';
import { StatusLabel } from 'src/domains/layouts/shared/labels/StatusLabel';
import dayjs from 'dayjs';

interface ListItemSecondaryPropsTypes {
    label: string;
    startDate?: string;
    endDate?: string;
    imgUrl: string;
    itemType?: string;
    isEdited: boolean;
    setPublished: () => Promise<void>;
    isPublished: boolean;
    handleDragAndDrop?: boolean;
    isDragged?: boolean;
    handleEdit?: () => void;
    handleDuplicate?: () => void;
    handleDelete: () => void;
    showDeletePopup?: boolean;
    handleHideDeletePopup?: () => void;
    handleShowDeletePopup?: () => void;
    dataTestItem?: string;
    dataTestLabel?: string;
    dataTestItemDescription?: string;
    /*** temporary props for move up/down until drag&drop option becomes available */
    handleMoveUpDown?: boolean;
    isDisabledUp?: boolean;
    isDisabledDown?: boolean;
    handleMoveUp?: () => void;
    handleMoveDown?: () => void;
}

export const ListItemPrimary = observer('ListItemPrimary', (props: ListItemSecondaryPropsTypes) => {
    const  {
        label,
        startDate,
        endDate,
        imgUrl,
        itemType,
        isEdited,
        setPublished,
        isPublished,
        handleDragAndDrop,
        isDragged,
        handleEdit,
        handleDuplicate,
        handleDelete,
        showDeletePopup,
        handleHideDeletePopup,
        handleShowDeletePopup,
        dataTestItem,
        dataTestLabel,
        dataTestItemDescription,
        handleMoveUpDown,
        isDisabledUp,
        isDisabledDown,
        handleMoveUp,
        handleMoveDown,
    } = props;

    const getStatusLabel = (): 'live' | 'scheduled' | 'ended' | '' => {
        const currentDate = dayjs();

        if (startDate === undefined || endDate === undefined) {
            return '';
        }

        if (currentDate.isBefore(dayjs(startDate))) {
            return 'scheduled';
        }

        if (currentDate.isAfter(dayjs(endDate))) {
            return 'ended';
        }

        return 'live';
    };

    const statusLabel = getStatusLabel();

    return (
        <ListItem direction='row' isEdited={isEdited} isDragged={isDragged} data-test={dataTestItem}>
            <LabelWrapper hasSubsection={false}>
                {handleMoveUpDown === true ?
                    <ColumnWrapper>
                        <ArrowIconWrapperTemporary isDisabled={isDisabledUp === true} onClick={handleMoveUp} position='up' />
                        <ArrowIconWrapperTemporary isDisabled={isDisabledDown === true} onClick={handleMoveDown} position='down' />
                    </ColumnWrapper> : null}
                {handleDragAndDrop === true ? <MovableSpan data-movable-handle>
                    <DragAndDropIconWrapper isDragged={isDragged ?? false} status={statusLabel} />
                </MovableSpan> : null}
                <ImageContainer imgSrc={imgUrl}>
                    {imgUrl === '' ?
                        <ImagePlaceholderIconWrapper /> : null
                    }
                </ImageContainer>
                <ItemDetails>
                    <span data-test={dataTestLabel}>{label}</span>
                    {itemType !== undefined || itemType !== '' ? <ItemDescription data-test={dataTestItemDescription}>{itemType}</ItemDescription> : null}
                </ItemDetails>
            </LabelWrapper>

            <StatusLabel status={statusLabel}>{statusLabel}</StatusLabel>

            <ActionsWrapper>
                <ToggleWrapper status={statusLabel} toggleActive={setPublished} isActive={statusLabel === 'ended' ? false : isPublished} dataTest='toggle'/>
                <MoreActions
                    onEdit={handleEdit}
                    onDuplicate={handleDuplicate}
                    showDeletePopup={showDeletePopup}
                    handleHideDeletePopup={handleHideDeletePopup}
                    handleShowDeletePopup={handleShowDeletePopup}
                    onDelete={handleDelete}
                    data-test='more-actions'
                />
            </ActionsWrapper>
        </ListItem>
    );
});
